/**
 * V8 铸剑阁 — 类型 + Mock 数据
 *
 * 设计准绳：design/v8/v8-001-forge-redesign.md
 * 数据来源：V7-007 课程设计 + V7-004 锻造叙事 V4
 *
 * V8 重构：新增章-节-关三层数据模型（ChapterData / SectionData / LevelItem）
 * 旧 MockLevel / MockStage 保留兼容，后续版本清理
 */

export type LevelState = "completed" | "current" | "available" | "locked";
export type KLevel = 1 | 2 | 3 | 4;

// ===== V8 新数据模型 =====

export interface ChapterData {
  num: number;                    // 1-5
  title: string;                  // "识兵" / "战术判断" / ...
  sub: string;                    // 副标题
  icon: string;                   // 章图标字符
  illusCls: string;               // 插图区 CSS 类
  state: "completed" | "current" | "locked";
  progress: { done: number; total: number };
  units: { name: string; cls: string }[];
  sections: SectionData[];
  exam: ExamData | null;
  story: StoryData | null;
  anchor?: AnchorData;            // 卡片间连接点
}

export interface SectionData {
  label: string;                  // "节 1.1 · 指令入门"
  story: { text: string } | null; // 节间剧情（可选）
  items: LevelItem[];
}

export interface LevelItem {
  id: string;                     // "1.1.1"
  name: string;
  teach: string;                  // 教学内容一行
  state: "completed" | "current" | "available" | "locked";
  rank?: string;                  // S/A/B/C
  units: string[];                // 兵种符号
}

export interface ExamData {
  name: string;
  desc: string;
  reward: string;
  state: "completed" | "available" | "locked";
  rank?: string;
}

export interface StoryData {
  label: string;
  lines: string[];
}

export interface AnchorData {
  tip: string;                    // tooltip 文本
  unlocked: boolean;
}

// ===== 旧数据模型（保留兼容） =====

export interface MockLevel {
  id: string;
  name: string;
  stage: 0 | 1 | 2 | 3 | 4;
  state: LevelState;
  narrative: string;
  teachingPoint: string;
  archetype: string;
  archetypeTactic: string;
  mapSize: string;
  mapType: string;
  promptExample: string;
  apiHint: string[];
}

export interface MockStage {
  num: 0 | 1 | 2 | 3 | 4;
  name: string;
  sub: string;
  meta: string;
  stagesComplete: number;
  stagesTotal: number;
  guidance: string;
  archetype: string;
}

export interface MockArchive {
  id: string;
  title: string;
  unlockKLevel: KLevel;
  unlocked: boolean;
  content: string;
  author?: string;
  relatedLevels: string[];
  relatedTech: string;
  isFiction: boolean;
}

export interface MockKLevel {
  level: KLevel;
  name: string;
  nameEn: string;
  state: "completed" | "current" | "locked";
  brief: string;
  narrative: string;
  gameMechanic: string;
  academicStage: string;
  academicDesc: string;
  examStatus: "passed" | "available" | "locked";
  examDate?: string;
}

export interface MockRecentRecord {
  levelId: string;
  levelName: string;
  date: string;
  grade: "S" | "A" | "B" | "C" | "D";
}

// ===== V8 CHAPTERS 主数据 =====

export const CHAPTERS: ChapterData[] = [
  // ═══════════════════ 章零·初次承刃（V16 新手引导） ═══════════════════
  {
    num: 0, title: "初次承刃", sub: "新手引导 · 校准演习", icon: "◇", illusCls: "card-illus-c0",
    state: "current",
    progress: { done: 0, total: 1 },
    units: [{ name: "坦克", cls: "tank" }],
    sections: [
      {
        label: "章零 · 校准演习场 #07",
        story: { text: '<strong>第零章：</strong>这是你的第一份战术方案。有人留了一段代码，但好像缺了什么。进去看看。' },
        items: [
          { id: "0.0.1", name: "校准演习场 #07", teach: "补全 shoot() 指令", state: "current", rank: "-", units: ["◈"] },
        ],
      },
    ],
    exam: null,
    story: {
      label: "首次推演",
      lines: [
        '<strong>承刃终端：</strong>方案 CH-00-07-0421-A 状态：待检阅。检测到缺口：缺少终结指令。',
        '<strong>教官 CH-00：</strong>"第一课，别急着赢——先学会看战场。"',
      ],
    },
    anchor: { tip: "章零·初次承刃", unlocked: true },
  },
  // ═══════════════════ 章一·识兵 ═══════════════════
  {
    num: 1, title: "识兵", sub: "单兵基础操控", icon: "◈", illusCls: "card-illus-c1",
    state: "completed",
    progress: { done: 5, total: 6 },
    units: [{ name: "坦克", cls: "tank" }],
    sections: [
      {
        label: "节 1.1 · 指令入门",
        story: null,
        items: [
          { id: "1.1.1", name: "初次接触", teach: "move 指令", state: "completed", rank: "S", units: ["◈"] },
          { id: "1.1.2", name: "发现敌人", teach: "scan + shoot 因果链", state: "completed", rank: "A", units: ["◈"] },
          { id: "1.1.3", name: "移动中开火", teach: "追踪 + 冷却管理", state: "completed", rank: "A", units: ["◈"] },
        ],
      },
      {
        label: "节 1.2 · 生存战术",
        story: { text: '<strong>导师简报：</strong>"记住——第一次推演的结果不重要。重要的是，你开始思考坦克的每一个决策了。"' },
        items: [
          { id: "1.2.1", name: "掩体意识", teach: "nearest_cover + 撤退", state: "completed", rank: "B", units: ["◈"] },
          { id: "1.2.2", name: "方向校准", teach: "距离 + 时机判断", state: "completed", rank: "S", units: ["◈"] },
          { id: "1.2.3", name: "冷却节奏", teach: "cooldown + 攻防切换", state: "available", rank: "-", units: ["◈"] },
        ],
      },
    ],
    exam: {
      name: "Exam-K2 · 探求者试炼", desc: "1×坦克 vs HUNTER·精英",
      reward: "晋升 K1→K2 · 解锁档案 #03-04", state: "completed", rank: "A",
    },
    story: {
      label: "战颅档案 #01-02 已解锁",
      lines: [
        '<strong>冯旸赫·2019：</strong>"兵棋推演不只是模拟战争。它是模拟决策——任何需要对抗性决策的领域，都是兵棋的战场。"',
        '<strong>战颅起源：</strong>战颅系统的核心不是更强的算法，而是"多智能体在对抗中涌现集体智能"的推演框架。',
      ],
    },
    anchor: { tip: "冯旸赫档案 #01-02 解锁\n战颅起源", unlocked: true },
  },
  // ═══════════════════ 章二·战术判断 ═══════════════════
  {
    num: 2, title: "战术判断", sub: "单兵进阶 · 无人机解锁", icon: "⬡", illusCls: "card-illus-c2",
    state: "current",
    progress: { done: 2, total: 5 },
    units: [{ name: "坦克", cls: "tank" }, { name: "无人机", cls: "drone" }],
    sections: [
      {
        label: "节 2.1 · 条件判断",
        story: null,
        items: [
          { id: "2.1.1", name: "条件初探", teach: "if/else 条件分支", state: "completed", rank: "A", units: ["◈"] },
          { id: "2.1.2", name: "血量博弈", teach: "血量阈值 + 撤退决策", state: "current", rank: "-", units: ["◈"] },
        ],
      },
      {
        label: "节 2.2 · 双兵入门",
        story: { text: '<strong>战颅日志：</strong>置信度下降至 72%。当战场变量超出已知模式，战颅的"算"开始犹豫——这是一个信号：你在进入它未覆盖的区域。' },
        items: [
          { id: "2.2.1", name: "坦克+无人机", teach: "双兵分工 · 侦察+攻击", state: "locked", rank: "-", units: ["◈", "✦"] },
          { id: "2.2.2", name: "多条件战术", teach: "三条件综合判断", state: "locked", rank: "-", units: ["◈", "✦"] },
        ],
      },
    ],
    exam: {
      name: "Exam-K3 · 认知者试炼", desc: "坦克+无人机 vs 2×SCALPEL·协同",
      reward: "晋升 K2→K3 · 解锁档案 #05-06", state: "locked",
    },
    story: {
      label: "进行中 · 导师引导",
      lines: [
        '<strong>导师简报：</strong>"有时候你看不到敌人——告诉坦克看不到时怎么办。这不是让你写完美方案，是让你学会面对不确定。"',
        '<strong>战颅状态：</strong>这是你进入铸剑阁以来，第一次感受到战颅系统不再无所不知的时刻。',
      ],
    },
    anchor: { tip: "淬火假说·线索\n第一代推演回顾", unlocked: false },
  },
  // ═══════════════════ 章三·双兵协同 ═══════════════════
  {
    num: 3, title: "双兵协同", sub: "编队作战 · 机器狗解锁", icon: "⬠", illusCls: "card-illus-c3",
    state: "locked",
    progress: { done: 0, total: 5 },
    units: [{ name: "坦克", cls: "tank" }, { name: "无人机", cls: "drone" }, { name: "机器狗", cls: "dog" }],
    sections: [
      {
        label: "节 3.1 · 侦察交战",
        story: { text: '<strong>战颅报告：</strong>双兵编队推演启动。无人机在前方提供视野，坦克负责火力——协同不是两个单兵相加，是涌现新能力。' },
        items: [
          { id: "3.1.1", name: "侦察先导", teach: "scan + 视野共享", state: "locked", rank: "-", units: ["◈", "✦"] },
          { id: "3.1.2", name: "协同打击", teach: "双兵夹击策略", state: "locked", rank: "-", units: ["◈", "✦"] },
        ],
      },
      {
        label: "节 3.2 · 侧翼对抗",
        story: { text: '<strong>战颅日志·置信度 68%：</strong>你开始主动使用 send_to 通信。战颅注意到你的思维模式已经从"指挥一台坦克"变成了"调度一支编队"。这是认知的跃迁——不是学习新 API，是改变了问题空间。' },
        items: [
          { id: "3.2.1", name: "侧翼编队", teach: "send_to 通信基础", state: "locked", rank: "-", units: ["◈", "✦"] },
          { id: "3.2.2", name: "反侦察战术", teach: "隐蔽 + 埋伏逻辑", state: "locked", rank: "-", units: ["◈", "✦", "◆"] },
          { id: "3.2.3", name: "状态切换", teach: "多策略优先级 · 状态机", state: "locked", rank: "-", units: ["◈", "✦", "◆"] },
        ],
      },
    ],
    exam: {
      name: "Exam-K4 · 觉醒者试炼-1", desc: "坦克+无人机+机器狗 vs 3×VANGUARD·协同",
      reward: "晋升 K3→K4 · 解锁档案 #07-08", state: "locked",
    },
    story: {
      label: "待解锁 · 淬火假说",
      lines: [
        '<strong>战颅档案 #07-08：</strong>当通信从硬编码变为自然语言，协同开始涌现——你发送的不再是坐标和指令，是【战术意图】。这是我们一直在等待的临界点。',
        '<strong>淬火假说·第二阶段线索：</strong>足够多的推演官在同一个系统中留下足够多的策略后，系统本身会产生"集体智能"。你现在正处于这一过程的中间——既在向系统贡献知识，也在从系统中汲取智慧。',
      ],
    },
    anchor: { tip: "战颅二号档案\n第二代推演回顾", unlocked: false },
  },
  // ═══════════════════ 章四·三兵混编 ═══════════════════
  {
    num: 4, title: "三兵混编", sub: "多方对抗 · 猫猫车解锁", icon: "⬢", illusCls: "card-illus-c4",
    state: "locked",
    progress: { done: 0, total: 3 },
    units: [{ name: "全部 4 种", cls: "" }],
    sections: [
      {
        label: "节 4.1 · 编队协同",
        story: { text: '<strong>战术简报：</strong>三兵编队是分水岭。坦克扛正面、无人机侦察、机器狗绕侧翼——你不再是编队的一员，你是编队的指挥。' },
        items: [
          { id: "4.1.1", name: "三兵编队", teach: "三角阵型 + 分工", state: "locked", rank: "-", units: ["◈", "✦", "◆"] },
          { id: "4.1.2", name: "火力支援", teach: "猫猫车远程支援", state: "locked", rank: "-", units: ["◈", "✦", "◆", "◇"] },
        ],
      },
      {
        label: "节 4.2 · 自主决策",
        story: { text: '<strong>⚠ 战颅系统·模式切换：</strong>从本关起，战颅将进入"静默模式"。不会再有 AI 建议、不会再有提示窗口。你之前依赖的每一步指引，现在都需要自己判断。"沉默不是放弃——是信任。它相信你已经有能力自己做决定。" — 导师留言' },
        items: [
          { id: "4.2.1", name: "战颅沉默", teach: "无 AI 建议独立决策", state: "locked", rank: "-", units: ["◈", "✦", "◆", "◇"] },
        ],
      },
    ],
    exam: {
      name: "Exam-K5 · 觉醒者试炼-2", desc: "4兵自由组合 vs 4×混合AI·协同",
      reward: "晋升 K4→K5 · 解锁档案 #09-10", state: "locked",
    },
    story: {
      label: "待解锁 · 觉醒者现象",
      lines: [
        '<strong>某次推演回放·异常片段：</strong>一个 Agent 做出了设计者没教过的策略。它的合作模式无法被任何单体设计者的能力所涵盖——推演记录显示，它从战场上其他 Agent 的行为中"学习"了一个新的绕后路线。',
        '<strong>导师评论：</strong>"这不是 bug。这就是淬火假说预测的现象——足够多的推演官在同一个系统中留下足够多的策略后，系统产生的不是更聪明的 AI，是更聪明的【集体】。你现在就是这个集体的一部分。"',
        '<strong>学长江如海：</strong>"我们之前怀疑这只是偶然。但你的推演数据也让系统出现了新的模式。我感觉我们快接近答案了——冯老师说的那个答案。"',
      ],
    },
    anchor: { tip: "觉醒者现象\n千手观音系统", unlocked: false },
  },
  // ═══════════════════ 章五·实战推演 ═══════════════════
  {
    num: 5, title: "实战推演", sub: "自由组合终局", icon: "✧", illusCls: "card-illus-c5",
    state: "locked",
    progress: { done: 0, total: 3 },
    units: [{ name: "自由组合", cls: "" }],
    sections: [
      {
        label: "节 5.1 · 自由组合",
        story: { text: '<strong>终局指令：</strong>战颅已切换至静默模式。没有建议，没有提示。这是你的战场——从零构思完整战术。' },
        items: [
          { id: "5.1.1", name: "自由编队", teach: "自选兵种组合", state: "locked", rank: "-", units: ["?"] },
          { id: "5.1.2", name: "完全自主", teach: "从零构思战术方案", state: "locked", rank: "-", units: ["?"] },
        ],
      },
      {
        label: "节 5.2 · 终局试炼",
        story: { text: '<strong>导师留言：</strong>"这不是考试。这是仪式。从见习者走到这里，你已经证明了人不会被 AI 替代——人会使用 AI 做出 AI 自己不会做的选择。去吧——推演不是终点，承刃才是。"' },
        items: [
          { id: "5.2.1", name: "终局推演", teach: "综合能力 + 个人风格", state: "locked", rank: "-", units: ["?"] },
        ],
      },
    ],
    exam: null,
    story: {
      label: "终局仪式 · 淬火假说",
      lines: [
        '<strong>冯旸赫备忘录：</strong>"当通信从硬编码变为自然语言，集体智能是否可能涌现？这是战颅留给承刃计划的最终问题。答案不在我的代码里——在你们的推演中。"',
        '<strong>淬火假说全文：</strong>足够多的推演官在同一个系统中留下足够多的策略后，系统本身会产生"集体智能"——不是任何单体 Agent 的智能，而是所有推演官的知识在对抗中涌现出的更高层次策略模式。',
      ],
    },
  },
];

// ===== 旧 Mock 数据（保留兼容，后续版本清理） =====

export const MOCK_LEVELS: MockLevel[] = [
  { id: "L0", name: "初次接触", stage: 0, state: "completed",
    narrative: "推演官，这是你的第一次战术推演。一个不会还手的对手，让你熟悉指令。",
    teachingPoint: "步进指令——move/scan/attack 基础 API",
    archetype: "静止靶", archetypeTactic: "站在原地不动，等你靠近",
    mapSize: "12×12", mapType: "开放场地",
    promptExample: '"告诉坦克：向前移动到坐标 (7,7)"',
    apiHint: ["move(target)", "scan()", "shoot()"] },
  { id: "L1", name: "发现敌人", stage: 0, state: "completed",
    narrative: "前方有敌人！它暂时不动，但它看到你会反击。",
    teachingPoint: "扫描-攻击因果链：发现 → 攻击",
    archetype: "静止靶", archetypeTactic: "不动，但会回击",
    mapSize: "12×12", mapType: "开放场地",
    promptExample: '"扫描前方，看到敌人就开火"',
    apiHint: ["scan()", "shoot()", "self.enemy"] },
  { id: "L2", name: "移动中开火", stage: 0, state: "completed",
    narrative: "敌人在移动——它直线来回跑动。你需要先靠近，再开火。",
    teachingPoint: "距离判断 + 持续追踪",
    archetype: "慢速移动靶", archetypeTactic: "沿水平线来回慢速移动",
    mapSize: "12×12", mapType: "开放场地",
    promptExample: '"追踪移动的敌人，进入射程就开火"',
    apiHint: ["move()", "scan()", "shoot()", "distance_to()"] },
  { id: "L3", name: "方向校准", stage: 1, state: "completed",
    narrative: "你的对手是一台高速突击型 Agent——猎手。它直线追击，不找掩体。",
    teachingPoint: "方向选择 + 距离时机",
    archetype: "HUNTER 猎手", archetypeTactic: "直线追击玩家，不找掩体",
    mapSize: "15×15", mapType: "2-3 掩体",
    promptExample: '"敌人不会动，你发现它了，你该做什么？"',
    apiHint: ["move()", "scan()", "shoot()", "cooldown_left"] },
  { id: "L4", name: "掩体意识", stage: 1, state: "current",
    narrative: "猎手加速追击——你需要学会利用掩体。受伤了？想想有什么可以挡住子弹。",
    teachingPoint: "掩体识别 + 撤退时机",
    archetype: "HUNTER 猎手·加速", archetypeTactic: "直线追击 + 加速",
    mapSize: "15×15", mapType: "3 掩体 + 补给点",
    promptExample: '"受伤了？想想有什么可以挡住子弹"',
    apiHint: ["move()", "scan()", "shoot()", "nearest_cover()", "health"] },
  { id: "L5", name: "冷却节奏", stage: 1, state: "locked",
    narrative: "开火后需要等待冷却——在这期间你该做什么？",
    teachingPoint: "冷却管理 + 战术切换",
    archetype: "HUNTER 猎手·强化", archetypeTactic: "加速追击 + 攻击间隔短",
    mapSize: "15×15", mapType: "掩体群",
    promptExample: '"开火后需要等待冷却——在这期间你该做什么？"',
    apiHint: ["cooldown_left", "scan()", "move()", "nearest_cover()"] },
  { id: "L6", name: "条件初探", stage: 2, state: "locked",
    narrative: "有时候你看不到敌人——告诉坦克看不到时怎么办。",
    teachingPoint: "条件判断基础",
    archetype: "SCALPEL 手术刀", archetypeTactic: "血量低于阈值撤退补给",
    mapSize: "15×15", mapType: "掩体 + 补给",
    promptExample: '"看不到敌人时——你该怎么搜索？"',
    apiHint: ["scan()", "move()", "cooldown_left"] },
  { id: "L7", name: "血量博弈", stage: 2, state: "locked",
    narrative: "敌人也会开火——你需要同时考虑攻击和躲避。",
    teachingPoint: "血量阈值 + 撤退决策",
    archetype: "SCALPEL 手术刀", archetypeTactic: "撤退 + 反击循环",
    mapSize: "15×15", mapType: "掩体群 + 补给",
    promptExample: '"血量低时该怎么做？敌人会撤退你该怎么做？"',
    apiHint: ["health", "nearest_cover()", "scan()", "shoot()"] },
  { id: "L8", name: "多条件战术", stage: 2, state: "locked",
    narrative: "敌人变聪明了——你需要想得更周全。",
    teachingPoint: "血量+冷却+距离多条件",
    archetype: "SCALPEL 手术刀·多阈值", archetypeTactic: "多阶段战术切换",
    mapSize: "20×20", mapType: "复杂地形",
    promptExample: '"综合考虑血量、冷却、距离做决策"',
    apiHint: ["health", "cooldown_left", "distance_to()", "nearest_cover()", "scan()"] },
  { id: "L9", name: "侦察交战", stage: 3, state: "locked",
    narrative: "不先找到敌人就冲过去，会发生什么？",
    teachingPoint: "侦察-交战-撤退三段式",
    archetype: "VANGUARD 先锋", archetypeTactic: "8 向移动 + 动态策略",
    mapSize: "20×20", mapType: "掩体 + 多路径",
    promptExample: '"受伤时不躲掩体会怎样？试试用掩体保护自己"',
    apiHint: ["scan()", "move()", "shoot()", "nearest_cover()", "health"] },
  { id: "L10", name: "状态切换", stage: 3, state: "locked",
    narrative: "在侦察/交战/撤退之间切换——状态机思维。",
    teachingPoint: "多策略优先级",
    archetype: "VANGUARD 先锋·动态", archetypeTactic: "动态策略切换",
    mapSize: "20×20", mapType: "复杂掩体群",
    promptExample: '"设计一个状态机：侦察→交战→撤退"',
    apiHint: ["scan()", "health", "cooldown_left", "move()", "shoot()", "nearest_cover()"] },
  { id: "L11", name: "侧翼思维", stage: 3, state: "locked",
    narrative: "正面火力太猛——有没有别的路？",
    teachingPoint: "路径规划 + 绕后",
    archetype: "VANGUARD 先锋·绕后", archetypeTactic: "侦察 + 绕侧翼",
    mapSize: "20×20", mapType: "多补给 + 侧翼通道",
    promptExample: '"正面火力太猛——有没有别的路？"',
    apiHint: ["move()", "scan()", "shoot()", "distance_to()"] },
  { id: "L12", name: "完整方案", stage: 4, state: "locked",
    narrative: "这是你的战场。你知道该怎么做。",
    teachingPoint: "从零构思完整战术方案",
    archetype: "混合 Archetype A", archetypeTactic: "前 4 阶段所有 AI 行为复合",
    mapSize: "20×20", mapType: "高复杂度",
    promptExample: '"这是你的战场。你知道该怎么做。"',
    apiHint: ["全部 19 个 API"] },
  { id: "L13", name: "终局推演", stage: 4, state: "locked",
    narrative: "API 参考面板已就绪。开始吧，演习先知。",
    teachingPoint: "综合能力 + 个人风格",
    archetype: "混合 Archetype B", archetypeTactic: "完全不可预测",
    mapSize: "25×25", mapType: "全地形",
    promptExample: '"API 参考面板已就绪。开始吧。"',
    apiHint: ["全部 19 个 API（含 send_to/recv）"] },
];

export const MOCK_STAGES: MockStage[] = [
  { num: 0, name: "指令觉醒",   sub: "AWAKENING",    meta: "12×12 · 静止靶 / 慢速移动",  stagesComplete: 3, stagesTotal: 3, guidance: "★★★★★", archetype: "静止靶 / 慢速移动靶" },
  { num: 1, name: "参数部署",   sub: "DEPLOYMENT",   meta: "15×15 · 2-3 掩体 · HUNTER",  stagesComplete: 1, stagesTotal: 3, guidance: "★★★★☆", archetype: "HUNTER 猎手·直线追击" },
  { num: 2, name: "逻辑编织",   sub: "WEAVING",      meta: "15×15 · 掩体群 · SCALPEL",   stagesComplete: 0, stagesTotal: 3, guidance: "★★★☆☆", archetype: "SCALPEL 手术刀·撤退反击" },
  { num: 3, name: "架构推演",   sub: "ARCHITECTURE", meta: "20×20 · 复杂地形 · VANGUARD", stagesComplete: 0, stagesTotal: 3, guidance: "★★☆☆☆", archetype: "VANGUARD 先锋·绕侧翼" },
  { num: 4, name: "空白征服",   sub: "CONQUEST",     meta: "25×25 · 全地形 · 混合 AI",    stagesComplete: 0, stagesTotal: 2, guidance: "★☆☆☆☆", archetype: "混合 Archetype" },
];

export const MOCK_KLEVELS: MockKLevel[] = [
  { level: 1, name: "见习者", nameEn: "NOVICE", state: "completed",
    brief: "1v1 单坦克 · 单智能体决策",
    narrative: "欢迎加入承刃计划。你的第一个任务：学会指挥一台模拟机体。",
    gameMechanic: "1v1 — 1 个推演官指挥 1 个 Agent",
    academicStage: "Single-Agent RL",
    academicDesc: "AlphaGo 时代 (2015-2017)。一个智能体在围棋上超越人类。核心问题：智能体如何做出最优决策？",
    examStatus: "passed", examDate: "2026-07-10" },
  { level: 2, name: "探求者", nameEn: "SEEKER", state: "current",
    brief: "2v2 双坦克 · 多智能体独立",
    narrative: "你不再执行指令——你开始部署战术意图。但记住：两个最优选择，不等于整体最优。",
    gameMechanic: "2v2 — 2 个独立 Agent 协同（无通信）",
    academicStage: "Independent MARL",
    academicDesc: "OpenAI Five / AlphaStar 时代 (2017-2020)。多个独立智能体在对抗中达成整体最优。Nash 均衡 ≠ 全局最优。",
    examStatus: "available" },
  { level: 3, name: "认知者", nameEn: "COGNIZER", state: "locked",
    brief: "3v3 三坦克 · 多智能体协同通信",
    narrative: "冯旸赫写道：'真正的智能不在单体 Agent 的参数里，在它们对抗的间隙之间。'通信改变一切。",
    gameMechanic: "3v3 — 3 个 Agent 协同 + send_to/recv 通信",
    academicStage: "CTDE + Agent Communication",
    academicDesc: "集中训练 + 分散执行 (2020-2024)。当 Agent 用自然语言通信时，协作从'预设'变成'涌现'。",
    examStatus: "locked" },
  { level: 4, name: "觉醒者", nameEn: "AWAKENED", state: "locked",
    brief: "涌现 · 设计规则而非编写指令",
    narrative: "淬火假说：当足够多的推演官留下知识，系统是否会产生集体智能？你现在知道答案了。",
    gameMechanic: "涌现 — LLM 推演 + Multi-Agent Emergence",
    academicStage: "LLM + Multi-Agent Emergence",
    academicDesc: "AgentVerse / AutoGen / CAMEL 时代 (2024-)。涌现协同效应——3 个弱 bot 合理通信可能战胜 3 个强 bot 不通信。",
    examStatus: "locked" },
];

export const MOCK_ARCHIVES: MockArchive[] = [
  { id: "#01", title: "冯旸赫·2019", unlockKLevel: 1, unlocked: true,
    content: "兵棋推演不只是模拟战争。它是模拟决策——任何需要对抗性决策的领域，都是兵棋的战场。",
    author: "冯旸赫, 2019",
    relatedLevels: ["L0"],
    relatedTech: "第一代推演回顾（K2 晋升时解锁）",
    isFiction: false },
  { id: "#02", title: "战颅起源", unlockKLevel: 1, unlocked: true,
    content: "战颅系统的核心不是更强的算法，而是'多智能体在对抗中涌现集体智能'的推演框架。这是 2016 年我们启动这个项目时的初心。",
    author: "战颅研发团队",
    relatedLevels: ["L0", "L1"],
    relatedTech: "战颅系统诞生（2019）",
    isFiction: false },
  { id: "#03", title: "淬火假说·线索", unlockKLevel: 2, unlocked: false,
    content: "推演官的每一次战术设计都在'淬火'这个系统。当足够多的推演官留下足够多的策略，集体智能是否可能涌现？",
    author: "冯旸赫, 2021",
    relatedLevels: ["L3", "L4"],
    relatedTech: "淬火假说（V7 §2.6.5）",
    isFiction: true },
  { id: "#04", title: "第一代推演回顾", unlockKLevel: 2, unlocked: false,
    content: "2019 年的战颅系统只能指挥单个作战单元。推演官需要为每个单元手动编写行为树。这是第一代推演的核心限制。",
    relatedLevels: ["L3"],
    relatedTech: "Single-Agent RL 时代",
    isFiction: true },
  { id: "#05", title: "战颅二号", unlockKLevel: 2, unlocked: false,
    content: "2021 年战颅二号：多智能体协同决策首次实现。但通信协议是硬编码的——协同靠预定义规则，而非涌现。",
    relatedLevels: ["L4", "L5"],
    relatedTech: "MARL 范式确立",
    isFiction: true },
  { id: "#06", title: "第二代推演回顾", unlockKLevel: 3, unlocked: false,
    content: "2021 年战颅二号的多智能体协同——AI 开始指挥多个单元，但通信协议是硬编码的，协同靠预定义规则。这是第二代的特征。",
    relatedLevels: ["L6", "L7"],
    relatedTech: "CTDE 范式",
    isFiction: true },
  { id: "#07", title: "觉醒者现象", unlockKLevel: 3, unlocked: false,
    content: "某次推演回放中，一个 Agent 做出了设计者没教过的策略。它的合作模式无法被任何单体设计者的能力所涵盖。",
    author: "战颅研究日志 #427",
    relatedLevels: ["L9", "L10"],
    relatedTech: "涌现协同效应",
    isFiction: true },
  { id: "#08", title: "千手观音系统", unlockKLevel: 3, unlocked: false,
    content: "千手观音是大规模多智能体协同的延伸。当 1000 个 Agent 同时行动时，'设计者'的概念已经失效——系统在'自我组织'。",
    relatedLevels: ["L11"],
    relatedTech: "大规模 MARL",
    isFiction: true },
  { id: "#09", title: "冯旸赫备忘录", unlockKLevel: 4, unlocked: false,
    content: "当通信从硬编码变为自然语言，集体智能是否可能涌现？这是战颅留给承刃计划的最终问题。答案不在我的代码里——在你们的推演中。",
    author: "冯旸赫, 最终备忘录",
    relatedLevels: ["L12"],
    relatedTech: "淬火假说全文",
    isFiction: true },
  { id: "#10", title: "淬火假说全文", unlockKLevel: 4, unlocked: false,
    content: "淬火假说：足够多的推演官在同一个系统中留下足够多的策略后，系统本身会产生'集体智能'——不是任何单体 Agent 的智能，而是所有推演官的知识在对抗中涌现出的更高层次策略模式。",
    author: "冯旸赫, 2023（最终修订）",
    relatedLevels: ["L13"],
    relatedTech: "LLM + Multi-Agent Emergence",
    isFiction: true },
];

export const MOCK_RECENT: MockRecentRecord[] = [
  { levelId: "L3", levelName: "方向校准",   date: "3 天前", grade: "S" },
  { levelId: "L2", levelName: "移动中开火", date: "5 天前", grade: "A" },
  { levelId: "L1", levelName: "发现敌人",   date: "1 周前", grade: "B" },
  { levelId: "L0", levelName: "初次接触",   date: "1 周前", grade: "A" },
];

export const STAGE_ARCHIVES: Record<number, string[]> = {
  0: ["#01", "#02"],
  1: ["#03", "#04"],
  2: ["#05", "#06"],
  3: ["#07", "#08"],
  4: ["#09", "#10"],
};

// ===== 全局常量 =====

export const CURRENT_K_LEVEL: KLevel = 2;
export const COMPLETED_COUNT = 6;
export const TOTAL_COUNT = 25;

/** forge ID → v5 关卡数据目录（data/levels/_builtin/{path}/） */
export const FORGE_LEVEL_MAP: Record<string, string> = {
  // Ch0 初次承刃（V16 新手引导）
  "0.0.1": "v0_first_forge",
  // Ch1 识兵
  "1.1.1": "v5_0-1_first_contact",
  "1.1.2": "v5_0-2_find_enemy",
  "1.1.3": "v5_0-3_moving_target",
  "1.2.1": "v5_1-2_cover_aware",
  "1.2.2": "v5_1-1_direction",
  "1.2.3": "v5_1-3_cooldown",
  // Ch2 战术判断
  "2.1.1": "v5_2-1_condition_basic",
  "2.1.2": "v5_2-2_hp_gambit",
  "2.2.1": "v5_forge_2_2_1_drone",
  "2.2.2": "v5_2-3_multi_condition",
  // Ch3 双兵协同
  "3.1.1": "v5_3-1_scout_engage",
  "3.1.2": "v5_forge_3_1_2_coord",
  "3.2.1": "v5_3-3_flank",
  "3.2.2": "v5_forge_3_2_2_counter",
  "3.2.3": "v5_3-2_state_switch",
  // Ch4 三兵混编
  "4.1.1": "v5_forge_4_1_1_triad",
  "4.1.2": "v5_forge_4_1_2_fire",
  "4.2.1": "v5_4-1_full_plan",
  // Ch5 实战推演
  "5.1.1": "v5_forge_5_1_1_free",
  "5.1.2": "v5_forge_5_1_2_auto",
  "5.2.1": "v5_4-2_final_exercise",
  // 晋升试炼
  "exam-1": "v5_forge_exam_k2",
  "exam-2": "v5_forge_exam_k3",
  "exam-3": "v5_forge_exam_k4",
  "exam-4": "v5_forge_exam_k5",
};  // V8: 17关 + 4考 + 终局 = 22
export const UNLOCKED_ARCHIVE_COUNT = 2;
export const TOTAL_ARCHIVE_COUNT = 10;
